import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Upload,
  Download,
  Copy,
  SlidersHorizontal,
  RefreshCw,
  User,
  Edit3,
  Trash2,
} from "lucide-react";
import {
  listContacts,
  createContact,
  updateContact,
  deleteContact,
  listTags,
  type CurrentUser,
  type Contact,
  type Tag,
} from "../../lib/api";

export function ContactsView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTagId, setSelectedTagId] = useState<string | "all">("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editOptIn, setEditOptIn] = useState("opted_in");

  // Form State for Add Contact
  const [phoneE164, setPhoneE164] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [csvText, setCsvText] = useState("");

  // Live Contacts Query
  const contactsQuery = useQuery({
    queryKey: ["contacts", searchQuery, selectedTagId, user.tenant_id],
    queryFn: () =>
      listContacts(
        token,
        searchQuery || undefined,
        selectedTagId === "all" ? undefined : selectedTagId,
        user.tenant_id
      ),
    refetchInterval: 10_000,
  });

  // Live Tags Query
  const tagsQuery = useQuery({
    queryKey: ["contacts", "tags", user.tenant_id],
    queryFn: () => listTags(token, user.tenant_id),
  });

  const contacts = contactsQuery.data || [];
  const tags = tagsQuery.data || [];

  // Create Contact Mutation
  const createContactMutation = useMutation({
    mutationFn: () =>
      createContact(
        token,
        {
          phone_e164: phoneE164.trim(),
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
          email: email.trim() || undefined,
        },
        user.tenant_id
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setIsAddModalOpen(false);
      setPhoneE164("");
      setFirstName("");
      setLastName("");
      setEmail("");
    },
    onError: (err: any) => {
      alert(`Failed to add contact: ${err.message || "Unknown error"}`);
    },
  });

  // Update Contact Mutation
  const updateContactMutation = useMutation({
    mutationFn: () => {
      if (!editingContact) throw new Error("No contact selected for edit");
      return updateContact(token, editingContact.id, {
        first_name: editFirstName.trim() || undefined,
        last_name: editLastName.trim() || undefined,
        email: editEmail.trim() || undefined,
        opt_in_status: editOptIn,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setEditingContact(null);
    },
    onError: (err: any) => {
      alert(`Failed to update contact: ${err.message || "Unknown error"}`);
    },
  });

  // Delete Contact Mutation
  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) => deleteContact(token, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (err: any) => {
      alert(`Failed to delete contact: ${err.message || "Unknown error"}`);
    },
  });

  // Bulk CSV Import Mutation
  const importMutation = useMutation({
    mutationFn: async (csvRaw: string) => {
      const lines = csvRaw.trim().split("\n");
      const parsedContacts: any[] = [];
      for (let i = 0; i < lines.length; i++) {
        const rawLine = lines[i];
        if (!rawLine) continue;
        const line = rawLine.trim();
        if (!line || (i === 0 && line.toLowerCase().includes("phone"))) continue;
        const parts = line.split(",").map((p) => p.trim().replace(/^"|"$/g, ""));
        const phone = parts[0];
        const fName = parts[1] || "";
        const lName = parts[2] || "";
        const em = parts[3] || "";
        if (phone && phone.length >= 6) {
          parsedContacts.push({
            phone_e164: phone.replace(/[^0-9+]/g, ""),
            first_name: fName,
            last_name: lName,
            email: em,
          });
        }
      }

      for (const item of parsedContacts) {
        await createContact(token, item, user.tenant_id);
      }
      return parsedContacts.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setIsImportModalOpen(false);
      setCsvText("");
      alert(`Successfully imported ${count} contacts!`);
    },
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  function exportCsv() {
    const headers = ["Phone,First Name,Last Name,Email,OptInStatus,CreatedAt"];
    const rows = contacts.map(
      (c) =>
        `"${c.phone_e164}","${c.first_name || ""}","${c.last_name || ""}","${c.email || ""}","${c.opt_in_status}","${c.created_at}"`
    );
    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `contacts_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <User className="w-6 h-6 text-emerald-600" />
            Contacts CRM
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Manage your audience database, WhatsApp opt-in statuses, and custom tags.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Upload className="w-3.5 h-3.5 text-slate-500" />
            <span>Import CSV</span>
          </button>

          <button
            onClick={exportCsv}
            className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Download className="w-3.5 h-3.5 text-slate-500" />
            <span>Export</span>
          </button>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add Contact</span>
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search contacts by name, phone, email..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Dotted Filter Chips */}
          <select
            value={selectedTagId}
            onChange={(e) => setSelectedTagId(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ All Tags ({tags.length})</option>
            {tags.map((t: Tag) => (
              <option key={t.id} value={t.id}>
                Tag: {t.name}
              </option>
            ))}
          </select>

          <button className="filter-chip">
            <span>⊕ Opt-in Status</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => contactsQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh database state"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${contactsQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Contacts Table (Clean eNotify Layout) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Contact Name</th>
                <th>Phone Number ↕</th>
                <th>Email</th>
                <th>Tags</th>
                <th>Opt-in Status ↕</th>
                <th>Created At ↕</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contactsQuery.isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-slate-400 text-xs">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                      <span className="font-semibold text-slate-600">Loading audience contacts...</span>
                      <span className="text-[11px] text-slate-400">Querying subscriber opt-in records and tag affiliations</span>
                    </div>
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400 text-xs">
                    No contacts found. Click <strong>+ Add Contact</strong> or <strong>Import CSV</strong> to build your WhatsApp audience.
                  </td>
                </tr>
              ) : (
                contacts.map((contact: Contact) => (
                  <tr key={contact.id} className="hover:bg-slate-50/70 transition-colors">
                    {/* Contact Name */}
                    <td>
                      <div className="font-bold text-slate-900 text-xs">
                        {contact.first_name || contact.last_name
                          ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
                          : "WhatsApp User"}
                      </div>
                    </td>

                    {/* Phone Number + Copy */}
                    <td>
                      <div className="flex items-center gap-1 font-mono text-xs font-semibold text-slate-800">
                        <button
                          onClick={() => copyText(contact.phone_e164)}
                          title="Copy phone"
                          className="hover:text-slate-900 text-slate-400"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        <span>{contact.phone_e164}</span>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="text-xs text-slate-600 font-mono">
                      {contact.email || "—"}
                    </td>

                    {/* Tags */}
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {contact.tags && contact.tags.length > 0 ? (
                          contact.tags.map((t) => (
                            <span
                              key={t.id}
                              className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200"
                            >
                              {t.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </div>
                    </td>

                    {/* Opt-in Status */}
                    <td>
                      <span
                        className={`status-pill ${
                          contact.opt_in_status === "opted_in"
                            ? "status-pill--connected"
                            : "status-pill--disconnected"
                        }`}
                      >
                        {contact.opt_in_status === "opted_in" ? "Opted In" : "Pending / Opted Out"}
                      </span>
                    </td>

                    {/* Created At */}
                    <td className="font-mono text-xs text-slate-500">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditingContact(contact);
                            setEditFirstName(contact.first_name || "");
                            setEditLastName(contact.last_name || "");
                            setEditEmail(contact.email || "");
                            setEditOptIn(contact.opt_in_status || "opted_in");
                          }}
                          title="Edit Contact"
                          className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => {
                            if (confirm(`Delete contact ${contact.phone_e164}?`)) {
                              deleteContactMutation.mutate(contact.id);
                            }
                          }}
                          disabled={deleteContactMutation.isPending}
                          title="Delete Contact"
                          className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Contact Modal */}
      {isAddModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                Add Contact to CRM
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  WhatsApp Phone Number (E.164) *
                </label>
                <input
                  type="text"
                  placeholder="+919323818985"
                  value={phoneE164}
                  onChange={(e) => setPhoneE164(e.target.value)}
                  className="modern-input w-full font-mono text-xs"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">First Name</label>
                  <input
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="modern-input w-full text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="modern-input w-full text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address</label>
                <input
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="modern-input w-full text-xs font-mono"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button className="btn-secondary text-xs" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => createContactMutation.mutate()}
                  disabled={!phoneE164.trim() || createContactMutation.isPending}
                >
                  {createContactMutation.isPending ? "Saving..." : "Save Contact"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {editingContact && (
        <div className="modal-backdrop" onClick={() => setEditingContact(null)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-emerald-600" />
                Edit Contact Details
              </h3>
              <button onClick={() => setEditingContact(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  WhatsApp Phone Number
                </label>
                <input
                  type="text"
                  value={editingContact.phone_e164}
                  className="modern-input w-full font-mono text-xs bg-slate-50 text-slate-500"
                  disabled
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">First Name</label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className="modern-input w-full text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className="modern-input w-full text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address</label>
                <input
                  type="email"
                  placeholder="contact@company.com"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className="modern-input w-full text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Opt-in Status</label>
                <select
                  value={editOptIn}
                  onChange={(e) => setEditOptIn(e.target.value)}
                  className="modern-input w-full text-xs"
                >
                  <option value="opted_in">Opted In (Active Subscriber)</option>
                  <option value="opted_out">Opted Out / Unsubscribed</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setEditingContact(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => updateContactMutation.mutate()}
                  disabled={updateContactMutation.isPending}
                >
                  {updateContactMutation.isPending ? "Saving..." : "Update Contact"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk CSV Import Modal */}
      {isImportModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsImportModalOpen(false)}>
          <div className="modal-card max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Upload className="w-4 h-4 text-emerald-600" />
                Bulk CSV Contact Import
              </h3>
              <button onClick={() => setIsImportModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Paste CSV data below with format: <code className="font-mono text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded">Phone, First Name, Last Name, Email</code>
              </p>

              <textarea
                placeholder={`+919323818985, Rajesh, Kumar, rajesh@example.com\n+919870759951, Aarti, Patel, aarti@example.com\n+918454818800, Deluxe, Client, deluxe@example.com`}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                className="modern-input w-full h-36 font-mono text-xs"
              />

              <div className="flex justify-end gap-2 pt-2">
                <button className="btn-secondary text-xs" onClick={() => setIsImportModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => importMutation.mutate(csvText)}
                  disabled={!csvText.trim() || importMutation.isPending}
                >
                  {importMutation.isPending ? "Importing..." : "Start Import"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
